/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Check, ChevronDown, FileDown, Link2, LogOut, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_brew-jobs/artifacts/kh66hzmr_The%20Brand%20Brew_White%20Logo.webp";
const api = axios.create({ baseURL: API, withCredentials: true });
const STATUSES = ["New", "Reviewed", "Shortlisted", "Rejected"];

const formatError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const FIELD_ROWS = [
  { key: "email", label: "Email", mailto: true },
  { key: "phone", label: "Phone" },
  { key: "agency_experience", label: "Agency experience" },
  { key: "agencies", label: "Agencies worked with" },
  { key: "portfolio", label: "Portfolio", link: true },
  { key: "resume_link", label: "Resume", link: true },
  { key: "software", label: "Software proficiency" },
  { key: "content_experience", label: "Content creation experience" },
  { key: "art_direction", label: "Art direction / production / design" },
  { key: "current_salary", label: "Current monthly in-hand salary" },
  { key: "expected_salary", label: "Expected monthly in-hand salary" },
  { key: "notice_period", label: "Notice period (Delhi, full-time)" },
  { key: "technical_project", label: "Technical project OK" },
  { key: "favorite_show", label: "Favourite TV show" },
  { key: "additional_info", label: "Additional information" },
];

function AdminPage() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [apps, setApps] = useState(null);
  const [error, setError] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState([]);

  useEffect(() => { document.title = "Admin — The Brand Brew Careers"; }, []);

  useEffect(() => {
    api.get("/admin/me")
      .then((res) => setAdmin(res.data))
      .catch(() => setAdmin(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!admin) return;
    api.get("/admin/applications")
      .then((res) => setApps(res.data))
      .catch(() => setApps([]));
  }, [admin]);

  const login = async (event) => {
    event.preventDefault();
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const { data } = await api.post("/admin/login", {
        email: form.get("email"),
        password: form.get("password"),
      });
      setAdmin(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch { /* session cleared locally regardless */ }
    setAdmin(false);
    setApps(null);
  };

  const setStatus = async (appId, status) => {
    try {
      await api.patch(`/admin/applications/${appId}/status`, { status });
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, status } : a)));
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const deleteApp = async (appId, name) => {
    if (!window.confirm(`Delete the application from ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/applications/${appId}`);
      setApps((prev) => prev.filter((a) => a.id !== appId));
      setSelected((prev) => prev.filter((id) => id !== appId));
      setExpanded((prev) => prev.filter((id) => id !== appId));
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const copyCareersLink = async () => {
    const url = `${window.location.origin}/`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const temp = document.createElement("textarea");
      temp.value = url;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand("copy");
      document.body.removeChild(temp);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const roleOptions = useMemo(() => {
    if (!apps) return [];
    return [...new Set(apps.map((a) => a.role).filter(Boolean))];
  }, [apps]);

  const visibleApps = useMemo(() => {
    if (!apps) return [];
    return roleFilter === "All" ? apps : apps.filter((a) => a.role === roleFilter);
  }, [apps, roleFilter]);

  const visibleIds = visibleApps.map((a) => a.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));

  const toggleSelect = (appId) => {
    setSelected((prev) => (prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]));
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (allVisibleSelected ? prev.filter((id) => !visibleIds.includes(id)) : [...new Set([...prev, ...visibleIds])]));
  };

  const toggleExpand = (appId) => {
    setExpanded((prev) => (prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]));
  };

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected application${selected.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      await api.post("/admin/applications/bulk-delete", { ids: selected });
      setApps((prev) => prev.filter((a) => !selected.includes(a.id)));
      setSelected([]);
      setExpanded([]);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  if (checking) {
    return <main className="admin-shell" data-testid="admin-loading"><p className="admin-loading-text">Loading…</p></main>;
  }

  if (!admin) {
    return (
      <main className="admin-shell admin-login-wrap" data-testid="admin-login-page">
        <form className="admin-login-card" onSubmit={login} data-testid="admin-login-form">
          <img className="admin-logo" src={LOGO_URL} alt="The Brand Brew logo" data-testid="admin-login-logo" />
          <p className="eyebrow">CAREERS / ADMIN</p>
          <h1>Back for the<br /><em>good stuff.</em></h1>
          <label>Email<input name="email" type="email" required placeholder="you@thebrandbrew.in" data-testid="admin-email-input" /></label>
          <label>Password<input name="password" type="password" required placeholder="••••••••" data-testid="admin-password-input" /></label>
          {error && <p className="form-error" data-testid="admin-login-error">{error}</p>}
          <button className="submit-button" type="submit" data-testid="admin-login-button">Log in <ArrowUpRight /></button>
          <a className="admin-back-link" href="/" data-testid="back-to-site-link">← Back to careers site</a>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <img className="admin-logo" src={LOGO_URL} alt="The Brand Brew logo" data-testid="admin-header-logo" />
          <div>
            <p className="eyebrow">CAREERS / ADMIN</p>
            <h1>Applications<span className="dot">.</span></h1>
          </div>
        </div>
        <div className="admin-actions">
          <button className="admin-copy-btn" onClick={copyCareersLink} data-testid="copy-careers-link-button">
            {copied ? <Check size={16} /> : <Link2 size={16} />} {copied ? "Link copied" : "Copy careers link"}
          </button>
          <a className="admin-export-btn" href={`${API}/admin/applications/export`} data-testid="export-csv-button">
            <FileDown size={16} /> Export CSV
          </a>
          <button className="admin-logout-btn" onClick={logout} data-testid="admin-logout-button">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      {error && <p className="form-error" data-testid="admin-error">{error}</p>}

      {apps === null ? (
        <p className="admin-loading-text" data-testid="applications-loading">Loading applications…</p>
      ) : apps.length === 0 ? (
        <div className="admin-empty" data-testid="applications-empty">
          <span>✦</span>
          <h2>No applications yet.</h2>
          <p>When candidates apply through the careers page, they'll show up here — newest first.</p>
        </div>
      ) : (
        <>
          <div className="admin-toolbar">
            <p className="admin-count" data-testid="applications-count">
              {visibleApps.length} of {apps.length} application{apps.length === 1 ? "" : "s"}
            </p>
            <div className="filter-chips" data-testid="role-filter-chips">
              {["All", ...roleOptions].map((r) => (
                <button
                  key={r}
                  className={`filter-chip ${roleFilter === r ? "active" : ""}`}
                  onClick={() => setRoleFilter(r)}
                  data-testid={`filter-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <label className="select-all-label">
              <input
                type="checkbox"
                className="row-check"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                data-testid="select-all-checkbox"
              />
              Select all
            </label>
            {selected.length > 0 && (
              <button className="bulk-delete-btn" onClick={bulkDelete} data-testid="bulk-delete-button">
                <Trash2 size={15} /> Delete selected ({selected.length})
              </button>
            )}
          </div>

          <div className="app-cards" data-testid="applications-table">
            {visibleApps.map((a) => {
              const isOpen = expanded.includes(a.id);
              const status = a.status || "New";
              return (
                <article key={a.id} className={`app-card ${isOpen ? "expanded" : ""}`} data-testid={`application-row-${a.id}`}>
                  <div className="app-card-head">
                    <input
                      type="checkbox"
                      className="row-check"
                      checked={selected.includes(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      aria-label={`Select application from ${a.name}`}
                      data-testid={`application-select-${a.id}`}
                    />
                    <button className="app-card-summary" onClick={() => toggleExpand(a.id)} data-testid={`application-expand-${a.id}`}>
                      <span className="app-card-title">
                        <strong>{a.name}</strong>
                        <span className="app-card-role">{a.role || "—"}</span>
                      </span>
                      <span className="app-card-right">
                        <span className={`status-pill status-${status.toLowerCase()}`} data-testid={`application-status-pill-${a.id}`}>{status}</span>
                        <span className="cell-date">{formatDate(a.submitted_at)}</span>
                        <ChevronDown size={18} className={isOpen ? "rotate" : ""} />
                      </span>
                    </button>
                  </div>
                  {isOpen && (
                    <div className="app-card-body" data-testid={`application-detail-${a.id}`}>
                      <dl className="app-card-fields">
                        {FIELD_ROWS.map((f) => (
                          <div className="app-field" key={f.key}>
                            <dt>{f.label}</dt>
                            <dd>
                              {f.link && a[f.key] ? (
                                <a href={a[f.key]} target="_blank" rel="noreferrer" data-testid={`application-${f.key}-${a.id}`}>Open <ArrowUpRight size={12} /></a>
                              ) : f.mailto ? (
                                <a href={`mailto:${a[f.key]}`} data-testid={`application-email-${a.id}`}>{a[f.key]}</a>
                              ) : (
                                a[f.key] || "—"
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      <div className="app-card-actions">
                        <select
                          className={`status-select status-${status.toLowerCase()}`}
                          value={status}
                          onChange={(e) => setStatus(a.id, e.target.value)}
                          data-testid={`application-status-${a.id}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button className="delete-btn" onClick={() => deleteApp(a.id, a.name)} aria-label={`Delete application from ${a.name}`} data-testid={`application-delete-${a.id}`}>
                          <Trash2 size={15} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </>
      )}
    </main>
  );
}
export default AdminPage;
